/**
 * useLatestDossierSignature — Fetches the most recent cryptographic
 * signature for an embarque's dossier (DIAN-03).
 *
 * Returns the latest record from `dossier_signatures` ordered by
 * `signed_at DESC`. Used to render the "Dossier Sealed" badge in the
 * shipment header and the CryptoAuditCard in the Aduanas tab.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface DossierSignatureRecord {
  id: string;
  signature: string;
  dossier_root_hash: string;
  signed_at: string;
  signed_by: string;
  key_id: string;
  manifest: any;
}

export function useLatestDossierSignature(embarqueId: string | null | undefined) {
  return useQuery({
    queryKey: ["dossier-signature", embarqueId],
    queryFn: async (): Promise<DossierSignatureRecord | null> => {
      const { data, error } = await supabase
        .from("dossier_signatures")
        .select("id, signature, dossier_root_hash, signed_at, signed_by, key_id, manifest")
        .eq("embarque_id", embarqueId!)
        .order("signed_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data as DossierSignatureRecord | null) ?? null;
    },
    enabled: !!embarqueId,
    staleTime: 30_000,
  });
}
